import { AutoDisableableDto, type AutoDisableableInterface } from '@zaoblako/shared/common/dtos/auto-disableable.dto';
import { HelpdeskTaskPriorityEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk.task.priority.enum';
import { HelpdeskPriorityI18nEnum } from '@zaoblako/shared/modules/helpdesk/i18n/helpdesk.priority.i18n.enum';
import { PlanningUnitFormDto, type PlanningUnitFormInterface } from '@zaoblako/shared/common/dtos/planning-unit/planning-unit-form.dto';
import { DeadlineFormDto, type DeadlineFormInterface } from '@zaoblako/shared/common/dtos/deadline/deadline-form.dto';
import { ChecklistTemplateFormDto, type ChecklistTemplateFormInterface } from '@zaoblako/shared/modules/project/dto/checklist-template/checklist-template-form.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { ProjectDtoKeysEnum } from '@zaoblako/shared/modules/project/enum/project-dto-keys.enum';
import { nonEmptyStringSchema } from '@zaoblako/shared/modules/serde/schemas/non-empty-string.schema';
import { commonEnumValidator } from '@zaoblako/shared/modules/serde/validators/common-enum.validator';
import { entityLikeSchemaStrict,  entityLikeSchema } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import { SecurityBackendValidatorsEnum } from '@zaoblako/shared/modules/security/enums/security-backend-validators.enum';
import { CatalogCheckersEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-checkers.enum';
import { ProjectCheckersEnum } from '@zaoblako/shared/modules/project/enum/project-checkers.enum';
import { BusinessCheckerKeysEnum } from '@zaoblako/shared/modules/business/enum/business-checker-keys.enum';
import { ClientCheckerKeysEnum } from '@zaoblako/shared/modules/client/enums/client-checker-keys.enum';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { MasterMilestoneFindInterface } from '@zaoblako/shared/modules/catalog/dtos/master-milestone/master-milestone-find.dto';
import type { ProjectTemplateFindInterface } from '@zaoblako/shared/modules/project/dto/template/project-template-find.dto';
import type { ProjectFindInterface } from '@zaoblako/shared/modules/project/dto/project/project-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import type { ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import type { FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import type { SerdeContextInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-context.interface';
import type { SerdeContextDataInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-context-data.interface';

import * as yup from 'yup';

/** Project template create and update APIs request body - DTO signature */
export interface ProjectTemplateFormInterface extends AutoDisableableInterface {
  /** Project name (with template variables) */
  name: string;
  /** Project description (with template variables) */
  description?: string;
  /** Project priority ({@link HelpdeskTaskPriorityEnum.MEDIUM} by default) */
  priority?: HelpdeskTaskPriorityEnum;
  /** Planning settings. Using only by root templates. */
  planning?: PlanningUnitFormInterface;
  /** Project deadline computation settings */
  deadline: DeadlineFormInterface;
  /** Responsible user */
  responsible: UserFindInterface;
  /** Master milestone to assign */
  masterMilestone?: MasterMilestoneFindInterface;
  /** Parent template. If defined */
  parent?: ProjectTemplateFindInterface;
  /** Parent project to assign during template usage */
  parentProject?: ProjectFindInterface;
  /** User group auditors */
  userGroupAuditors?: UserGroupFindInterface[];
  /** User auditors */
  userAuditors?: UserFindInterface[];
  /** Checklists tempaltes */
  checklists: ChecklistTemplateFormInterface[];
  /** Deals to assign */
  deals?: DealFindInterface[];
  /** Client to assign */
  client?: ClientFindInterface;
  /** Direction to assign */
  direction?: DirectionFindInterface;
  /** Unit to assign */
  unit?: UnitFindInterface;
  /** Filial to assign */
  filial?: FilialFindInterface;
}

/** Project template create and update APIs request body - DTO schema */
export const ProjectTemplateFormDto = SerdeDtoClass.from<AutoDisableableInterface, ProjectTemplateFormInterface>(
  AutoDisableableDto,
  ProjectDtoKeysEnum.PROJECT_TEMPLATE_FORM_DTO,
  {
    name: {
      label: 'Название',
      serializer: nonEmptyStringSchema,
    },
    description: {
      optional: true,
      label: 'Описание',
      serializer: yup.string(),
    },
    priority: {
      optional: true,
      default: HelpdeskTaskPriorityEnum.MEDIUM,
      label: 'Приоритет',
      serializer: commonEnumValidator(HelpdeskTaskPriorityEnum, HelpdeskPriorityI18nEnum),
    },
    planning: {
      optional: (context: SerdeContextInterface<SerdeContextDataInterface, ProjectTemplateFormInterface>) => !context?.value?.parent?.id,
      label: 'Настройки планирования',
      nested: PlanningUnitFormDto,
      serializer: (_, nested) => nested,
    },
    deadline: {
      label: 'Настройки дедлайна',
      nested: DeadlineFormDto,
      serializer: (_, nested) => nested,
    },
    responsible: {
      label: 'Ответственный',
      serializer: entityLikeSchemaStrict,
      checkers: [SecurityBackendValidatorsEnum.USER_ASSERT_EXISTS_BY_DTO],
    },
    masterMilestone: {
      optional: true,
      label: 'Мастер-веха',
      serializer:  entityLikeSchema,
      checkers: [CatalogCheckersEnum.MASTER_MILESTONE_ASSERT_EXISTS_BY_DTO],
    },
    parent: {
      optional: true,
      label: 'Родительский шаблон',
      serializer: entityLikeSchema,
      checkers: [ProjectCheckersEnum.PROJECT_TEMPLATE_ASSERT_EXISTS_BY_DTO],
    },
    parentProject: {
      optional: true,
      label: 'Надзадача',
      serializer: entityLikeSchema,
      checkers: [ProjectCheckersEnum.PROJECT_ASSERT_EXISTS_BY_DTO],
    },
    userGroupAuditors: {
      optional: true,
      label: 'Группы-аудиторы',
      serializer: yup.array(entityLikeSchema),
      checkers: [SecurityBackendValidatorsEnum.USER_GROUP_ASSERT_EXISTS_BY_DTO],
    },
    userAuditors: {
      optional: true,
      label: 'Пользователи-аудиторы',
      serializer: yup.array(entityLikeSchema),
      checkers: [SecurityBackendValidatorsEnum.USER_ASSERT_EXISTS_BY_DTO],
    },
    checklists: {
      label: 'Шаблоны событий',
      nested: ChecklistTemplateFormDto,
      serializer: (_, nested) => yup.array(nested),
    },
    deals: {
      optional: true,
      label: 'Сделки',
      serializer: yup.array(entityLikeSchema),
      checkers: [BusinessCheckerKeysEnum.DEAL_ASSERT_EXISTS_BY_DTO],
    },
    client: {
      optional: true,
      label: 'Клиент',
      serializer: entityLikeSchema,
      checkers: [ClientCheckerKeysEnum.CLIENT_ASSERT_EXISTS_BY_DTO],
    },
    direction: {
      optional: true,
      label: 'БС',
      serializer: entityLikeSchema,
      checkers: [CatalogCheckersEnum.DIRECTION_ASSERT_EXISTS_BY_DTO],
    },
    unit: {
      optional: true,
      label: 'БЕ',
      serializer: entityLikeSchema,
      checkers: [CatalogCheckersEnum.UNIT_ASSERT_EXISTS_BY_DTO],
    },
    filial: {
      optional: true,
      label: 'Филиал',
      serializer: entityLikeSchema,
      checkers: [CatalogCheckersEnum.FILIAL_ASSERT_EXISTS_BY_DTO],
    },
  },
);
